export interface GuideItem {
  heading: string;
  body:    string;
}

export interface GuideSection {
  id:    string;
  title: string;
  items: GuideItem[];
}

export const GUIDE_SECTIONS: GuideSection[] = [
  {
    id:    "getting-started",
    title: "Getting Started",
    items: [
      {
        heading: "Navigating the Portal",
        body: "Use the sidebar to switch between pages. The top section covers core data (People, Certificates, Events) and operations (Import, Reports, Audit Log). The Support section at the bottom contains this Help Center and the FAQ.",
      },
      {
        heading: "Switching Portals",
        body: "If you have access to both Find Your Ohio and AI Ready Ohio, use the portal switcher at the top of the sidebar to toggle between them.",
      },
    ],
  },
  {
    id:    "importing-certificates",
    title: "Importing Certificates",
    items: [
      {
        heading: "Preparing Your File",
        body: "Your CSV or XLSX file must include at minimum three columns: email, certificate_name, and earned_date. Optional columns include full_name, linkedin_url, company, event_name, and event_date. Download the sample templates from the Import page if you need a starting point.",
      },
      {
        heading: "Choosing a Certificate Type",
        body: "Select 'AI Week' if all rows in the file come from an Ohio AI Week event. Select 'External' if they come from a third-party provider. Use 'Auto-detect' (default) to let the system determine the type per row based on whether event columns are present.",
      },
      {
        heading: "Monitoring Import Progress",
        body: "After submitting a file, the page polls the backend every few seconds and displays a live status indicator. Once processing completes, you will see a summary of rows inserted, skipped (duplicates), and any errors. Switch to the Upload History tab to review past imports.",
      },
    ],
  },
  {
    id:    "managing-people",
    title: "Managing People",
    items: [
      {
        heading: "Finding a Person",
        body: "Use the search bar and filters on the People page to narrow by certificate name, event, tracking status, or whether a LinkedIn URL is on file. Click a row to open the Person Detail page.",
      },
      {
        heading: "Adding a LinkedIn URL",
        body: "If a person is missing a LinkedIn URL, their tracking will be paused. Open the Person Detail page and click '+ Add LinkedIn URL' next to the relevant certificate to enter the URL and resume tracking.",
      },
      {
        heading: "Editing Person Information",
        body: "From the Person Detail page, click the edit icon next to the person's name or LinkedIn URL. Save your changes to update the record.",
      },
    ],
  },
  {
    id:    "managing-certificates",
    title: "Managing Certificates",
    items: [
      {
        heading: "Editing a Certificate",
        body: "Open the Certificate Detail page and expand the 'Edit Certificate' panel. You can update the certificate name, earned date, external ID, company at enrollment, tracking status, stop reason, track-until date, next snapshot due date, and manual review flag.",
      },
      {
        heading: "Stopping or Pausing Tracking",
        body: "Change the Tracking Status to 'Paused' or 'Stopped' and provide a stop reason. If company changed is set to 'Yes', the status is automatically locked to Stopped.",
      },
      {
        heading: "Reactivating a Certificate",
        body: "From the Person Detail page, click the Reactivate button next to a stopped or paused certificate. This clears the stop reason, resets consecutive scrape failures, and schedules the next snapshot.",
      },
    ],
  },
  {
    id:    "reports-audit",
    title: "Reports & Audit",
    items: [
      {
        heading: "Using the Reports Page",
        body: "The Reports page shows a summary of total persons tracked, active tracking windows, and the percentage whose company changed. A breakdown by certificate name is also available. Use the Export button to download the full dataset as a CSV.",
      },
      {
        heading: "Using the Audit Log",
        body: "Every insert and update to certificates, persons, and related records is logged. Use the Audit Log page to filter by table or record ID and inspect previous and new values side by side.",
      },
    ],
  },
  {
    id:    "getting-help",
    title: "Getting Help",
    items: [
      {
        heading: "Contacting Support",
        body: "For technical issues or questions not covered here, reach out to the platform administrator. Include the relevant record ID (person ID or certificate ID) and a description of the issue to speed up diagnosis.",
      },
      {
        heading: "Reporting a Data Error",
        body: "If you believe a certificate record contains incorrect data, use the Certificate Detail edit form to correct it. All changes are logged in the Audit Log so there is always a record of what was changed and by whom.",
      },
    ],
  },
];
